import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Building2, Calendar, Users, Percent, MapPin,
  ExternalLink, ShieldAlert, Share2, FileText, AlertCircle,
  Info, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import ExplainButton from '../components/ExplainButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'resultado',    label: 'Resultado' },
  { key: 'metodologia',  label: 'Metodologia' },
  { key: 'contratante',  label: 'Contratante' },
  { key: 'documentos',   label: 'Documentos' },
  { key: 'fonte',        label: 'Fonte' },
];

const PesquisaDetalhe = () => {
  const { registro } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('resultado');

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr('');
      try {
        const r = await axios.get(`${API}/public/pesquisas/${encodeURIComponent(registro)}`, { timeout: 20000 });
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || 'Não foi possível carregar a pesquisa.');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [registro]);

  const share = async () => {
    const url = window.location.href;
    const text = `Pesquisa oficial nº ${registro} — organizada no Tô de Olho.`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Tô de Olho', text, url }); return; } catch (_) {}
    }
    try {
      await navigator.clipboard?.writeText(`${text}\n${url}`);
      toast.success('Link copiado.');
    } catch (_) { toast.info(`Copie manualmente: ${url}`); }
  };

  if (loading) return <div className="text-center py-10 text-sm text-[#475569]" data-testid="pesquisa-loading">Carregando pesquisa...</div>;

  if (err) return (
    <div className="max-w-3xl mx-auto py-10" data-testid="pesquisa-error">
      <Card className="p-6 border border-[#FCA5A5] bg-[#FEE2E2]">
        <div className="flex items-start gap-2 text-[#991B1B]">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-bold">Não foi possível carregar.</p>
            <p className="text-sm mt-1">{err}</p>
            <Button variant="outline" onClick={() => navigate('/cenario-eleitoral')} className="mt-3">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  if (!data) return null;
  const p = data.pesquisa || {};
  const cardUrl = `${API}/public/pesquisas/${encodeURIComponent(p.nr_pesquisa_tse)}/card.png`;

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="pesquisa-page">
      <button type="button" onClick={() => navigate('/cenario-eleitoral')} className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#006030]" data-testid="pesquisa-back">
        <ArrowLeft className="w-4 h-4" /> Voltar para lista
      </button>

      {/* Header */}
      <Card className="p-5 border border-[#E2E8F0]" data-testid="pesquisa-header">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#112240] flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-[#64C8F0]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[#94A3B8] uppercase tracking-wider">Pesquisa Nº {p.nr_pesquisa_tse}</p>
            <h1 className="text-lg md:text-2xl font-black text-[#0F1C2E] leading-tight break-words" data-testid="pesquisa-instituto">{p.instituto}</h1>
            <p className="text-xs text-[#475569] mt-1">
              {p.cargo} · {p.turno ? `${p.turno}º turno` : 'turno único'} · {p.abrangencia} · {p.uf}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap w-full sm:w-auto sm:self-start">
            <Button variant="outline" size="sm" onClick={share} className="border-[#006030] text-[#006030] hover:bg-[#E8F5E9] flex-1 sm:flex-none" data-testid="pesquisa-share-btn">
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Compartilhar
            </Button>
            <a href={cardUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[#006030] text-[#006030] rounded-md hover:bg-[#E8F5E9]" data-testid="pesquisa-card-btn">
              <ImageIcon className="w-3.5 h-3.5" /> Card
            </a>
          </div>
        </div>
      </Card>

      {/* Aviso */}
      <Card className="p-3 border-2 border-[#F59E0B] bg-[#FEF3C7]" data-testid="pesquisa-aviso">
        <div className="flex items-start gap-2">
          <ShieldAlert className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
          <p className="text-xs text-[#78350F] leading-relaxed">
            {data.aviso_fonte || 'Dados cadastrais oficiais do TSE. O Tô de Olho não produz esta pesquisa.'}
          </p>
        </div>
      </Card>

      {/* KPIs principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pesquisa-kpis">
        <KPI icon={<Calendar />} label="Divulgada" value={p.data_divulgacao || '-'} />
        <KPI icon={<Users />} label="Amostra" value={p.tamanho_amostra ? String(p.tamanho_amostra) : '-'} />
        <KPI icon={<Percent />} label="Margem de erro" value={p.margem_erro != null ? `± ${p.margem_erro}%` : '-'} />
        <KPI icon={<Percent />} label="Confiança" value={p.nivel_confianca != null ? `${p.nivel_confianca}%` : '-'} />
      </div>

      {/* Botão Explique da IA */}
      <div className="flex justify-start">
        <ExplainButton
          contexto={`pesquisa eleitoral nº ${p.nr_pesquisa_tse} do ${p.instituto} para ${p.cargo}, amostra de ${p.tamanho_amostra || '-'} entrevistados, margem de erro ± ${p.margem_erro || '-'}% e nível de confiança ${p.nivel_confianca || '-'}%. Metodologia: ${p.metodologia || '-'}`}
          tipo="pesquisa"
          fonte="TSE"
          entidade={p.instituto}
          testId="explain-pesquisa"
        />
      </div>

      {/* Abas */}
      <div className="border-b border-[#E2E8F0] flex gap-1 overflow-x-auto scrollbar-none" data-testid="pesquisa-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? 'border-[#006030] text-[#006030]' : 'border-transparent text-[#475569] hover:text-[#0F1C2E]'}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Conteúdo das abas */}
      {tab === 'resultado' && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="tab-content-resultado">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Resultado percentual por candidato</h2>
          <div className="bg-[#F8FAFC] p-4 rounded-md border border-dashed border-[#94A3B8] text-center">
            <Info className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
            <p className="text-sm text-[#475569] mb-2">
              Os resultados percentuais desta pesquisa estão no <strong>documento oficial do TSE</strong>.
            </p>
            <p className="text-xs text-[#94A3B8] mb-4">
              O Tô de Olho ainda não publica resultados por candidato. Estamos construindo um processo de validação manual para evitar erros de correspondência.
            </p>
            {data.documentos_oficiais?.pesqele && (
              <a
                href={data.documentos_oficiais.pesqele}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#006030] font-semibold hover:underline"
                data-testid="link-pesqele"
              >
                <ExternalLink className="w-4 h-4" /> Ver documento oficial no PesqEle
              </a>
            )}
          </div>
        </Card>
      )}

      {tab === 'metodologia' && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="tab-content-metodologia">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Metodologia declarada</h2>
          <dl className="space-y-3 text-sm">
            <FieldRow label="Método de coleta" value={p.metodologia || '-'} />
            <FieldRow label="Plano amostral" value={p.plano_amostral || '-'} />
            <FieldRow label="Estatístico responsável" value={p.estatistico_responsavel || '-'} />
            <FieldRow label="Período de coleta" value={p.data_inicio_coleta && p.data_fim_coleta ? `${p.data_inicio_coleta} até ${p.data_fim_coleta}` : '-'} />
            <FieldRow label="Data de divulgação" value={p.data_divulgacao || '-'} />
          </dl>
        </Card>
      )}

      {tab === 'contratante' && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="tab-content-contratante">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Contratante e pagante</h2>
          <p className="text-sm text-[#475569] mb-3">
            Os dados de contratante e pagante da pesquisa estão nos arquivos oficiais do TSE. Consulte o registro completo:
          </p>
          {data.documentos_oficiais?.pesqele && (
            <a href={data.documentos_oficiais.pesqele} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#006030] font-semibold hover:underline" data-testid="link-contratante">
              <ExternalLink className="w-4 h-4" /> Ver contratantes no PesqEle
            </a>
          )}
        </Card>
      )}

      {tab === 'documentos' && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="tab-content-documentos">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Documentos oficiais</h2>
          <ul className="space-y-2">
            {data.documentos_oficiais?.pesqele && (
              <li>
                <a href={data.documentos_oficiais.pesqele} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#006030] hover:underline" data-testid="link-doc-pesqele">
                  <FileText className="w-4 h-4" /> PesqEle · registro nº {p.nr_pesquisa_tse}
                </a>
              </li>
            )}
            <li>
              <a href="https://dadosabertos.tse.jus.br/dataset/pesquisas-eleitorais-2026" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#006030] hover:underline" data-testid="link-doc-ckan">
                <FileText className="w-4 h-4" /> Portal de Dados Abertos do TSE
              </a>
            </li>
          </ul>
        </Card>
      )}

      {tab === 'fonte' && (
        <Card className="p-5 border border-[#E2E8F0]" data-testid="tab-content-fonte">
          <h2 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">Fonte e atualização</h2>
          <dl className="space-y-3 text-sm">
            <FieldRow label="Origem" value={data.data_source || 'TSE'} />
            <FieldRow label="Última atualização no banco" value={p.atualizado_em ? new Date(p.atualizado_em).toLocaleString('pt-BR') : '-'} />
            <FieldRow label="Consultada em" value={data.consultada_em ? new Date(data.consultada_em).toLocaleString('pt-BR') : '-'} />
          </dl>
          <div className="mt-4 pt-4 border-t border-[#F1F5F9] text-xs text-[#94A3B8]">
            <p className="mb-2">Encontrou informação incorreta? Envie um e-mail para <a href="mailto:suporte@todeolho.inf.br" className="text-[#006030] font-semibold">suporte@todeolho.inf.br</a> com o número do registro.</p>
            <p>Metodologia do Tô de Olho: baixamos diariamente o Portal de Dados Abertos do TSE e organizamos os metadados. Não alteramos, ponderamos ou complementamos os dados originais.</p>
          </div>
        </Card>
      )}
    </div>
  );
};

const KPI = ({ icon, label, value }) => (
  <Card className="p-3 border border-[#E2E8F0]" data-testid={`pesquisa-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-1.5 text-[#475569] text-[10px] font-bold uppercase tracking-wider">
      {React.cloneElement(icon, { className: 'w-3 h-3' })} {label}
    </div>
    <p className="text-base md:text-lg font-black text-[#0F1C2E] mt-1 break-words">{value}</p>
  </Card>
);

const FieldRow = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
    <dt className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider">{label}</dt>
    <dd className="text-sm text-[#0F1C2E] sm:col-span-2">{value}</dd>
  </div>
);

export default PesquisaDetalhe;
